import { Injectable, Inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';
import { OktaAuth } from '@okta/okta-auth-js';
import { OKTA_AUTH } from '@okta/okta-angular';
import { AuthService } from 'src/app/auth/auth.service';
import { StorageService } from '../shared/services/storage.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard implements CanActivate {
  constructor(@Inject(OKTA_AUTH) private oktaAuth: OktaAuth, private appService: AuthService, private router: Router, private storageService: StorageService) {}

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean> {
    const isAuthenticated = await this.oktaAuth.isAuthenticated();
    if (!isAuthenticated) {
      // If user is not authenticated redirect to original path and remove earlier user details from IndexedDB
      await this.oktaAuth.signInWithRedirect({ originalUri: route.routeConfig?.path });
      await this.storageService.removeItem('userDetails');
      await this.storageService.removeItem('userPermissions');
      await this.storageService.removeItem('userRoles');
      return false;
    }

    // If user is authenticated from OKTA then fetch user details from IndexedDB which was setup through login-callback component
    const userDetails = await this.storageService.getItem('userDetails');
    const userPermissions = await this.storageService.getItem('userPermissions');
    const userRoles = await this.storageService.getItem('userRoles');
    // Setup app service states for the same user
    if (userDetails && userPermissions && userRoles) {
      this.appService.userDetails = userDetails;
      this.appService.userPermissions = userPermissions;
      this.appService.userRoles = userRoles;
    }
      
    const requiredRoles = route.data['requiredRoles'] as string[];
    const roles = this.appService.userRoles;
    // If user don't have any role assigned then do not allow for current route
    if (!roles || !roles.length) {
      return false;
    }

    // If user role matches with required role of current path then allow access for current route
    const hasRole = requiredRoles.some(role => roles.includes(role));
    if (hasRole) {
      return true;
    }

    // Else navigate to unauthorised page
    this.router.navigate(['/app/unauthorized']);
    return false;
  }
}
